import { useCallback, useEffect } from 'react';

import { LOCATION_CHANGE } from 'utils/routerRedux';
import { TooLazyToType } from 'types/TooLazyToType';
import {
  CLEAR_ALL_TOASTS,
  CREATE_TOAST,
} from 'containers/ToastNotificationContainer/constants';
import { assertUnreachable } from 'types/utilityTypes';

import { Toast } from './types';

type CreateToastAction = {
  type: typeof CREATE_TOAST;
  kind: 'success' | 'error' | 'warning' | 'info';
  message: string;
  options: TooLazyToType;
};

type ClearAllToastsAction = {
  type: typeof CLEAR_ALL_TOASTS;
};

// for legacy pushWithToast actions
type LocationChangeAction = {
  type: typeof LOCATION_CHANGE;
  payload?: {
    state?: {
      toast?: CreateToastAction;
    };
  };
};

// This file contains the logic required to trigger new-style toasts from legacy redux-based toast actions.
// Once all code calls the new `useToasts` hook instead, we can remove this file and all calls to its exports.
// To keep the entanglement minimum, we use `window.dispatchEvent` to fire events from redux into the new stack.
// See comments on the exports for more details.

/**
 * Custom ToastEvent - only to be used by the adapter, and to be sent through `window.dispatchEvent()`.
 *
 * eport for unit testing.
 */
export class ToastEvent extends CustomEvent<void> {
  constructor(public payload: CreateToastAction | ClearAllToastsAction) {
    super('toastEvent');
    this.payload = payload;
  }
}

/**
 * "Pseudo"-saga code to be called on certain toast-related actions.
 * For any incoming toast action, It'll create the appropriate `ToastEvent` and call `window.dispatchEvent()`
 *
 * @param action - the redux action we want to adapt
 */
export function* triggerToastFromSaga<
  Action extends CreateToastAction | ClearAllToastsAction | LocationChangeAction
>(action: Action) {
  const hasToast = (locationChange: LocationChangeAction) => {
    const routePayLoad = locationChange.payload;
    return routePayLoad && routePayLoad.state && routePayLoad.state.toast;
  };

  switch (action.type) {
    case LOCATION_CHANGE: {
      if (hasToast(action)) {
        const { kind, message, options } = action.payload.state.toast;
        yield window.dispatchEvent(
          new ToastEvent({
            type: CREATE_TOAST,
            kind,
            message,
            options,
          })
        );
      }
      break;
    }
    case CREATE_TOAST: {
      const { kind, message, options } = action;
      yield window.dispatchEvent(
        new ToastEvent({ type: action.type, kind, message, options })
      );
      break;
    }
    case CLEAR_ALL_TOASTS: {
      yield window.dispatchEvent(new ToastEvent({ type: action.type }));
      break;
    }
    default: {
      assertUnreachable(action);
    }
  }
}

/**
 * UseEffect wrapper used by ToastProvider to react to `ToastEvent`s using `window.addEventListener()`.
 * On an incoming event, it'll trigger the respective handler method to show or clear toasts.
 *
 * @param handler
 */
export const useLegacyToastAdapter = (handler: {
  showToast: (toast: Toast) => void;
  clearToasts: () => void;
}) => {
  const onToastEvent = useCallback(
    (event: ToastEvent) => {
      switch (event.payload.type) {
        case CLEAR_ALL_TOASTS:
          handler.clearToasts();
          break;
        case CREATE_TOAST: {
          const toast: Toast = {
            type: event.payload.kind,
            message: event.payload.message,
            key: event.payload.message,
            // FIXME dismissAfterSeconds: event.payload.options.dismissAfterSeconds,
            // TODO other options
          };
          handler.showToast(toast);
          break;
        }
        default:
          assertUnreachable(event.payload);
      }
    },
    [handler]
  );

  useEffect(() => {
    window.addEventListener('toastEvent', onToastEvent);

    return () => {
      window.removeEventListener('toastEvent', onToastEvent);
    };
  }, [onToastEvent]);
};
